import { Grid, CardHeader, CardContent, Box, TextField, Checkbox, FormControlLabel, Typography, FormControl, Select, MenuItem, Autocomplete, Button } from "@mui/material";
import React, { useState, useEffect, useCallback, useTransition } from "react";
import { gameManagementApi, gamePointManagementApi } from "../../services/AllServices";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import errorHandler from "../../utils/errorHandler";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { GameSchema } from "../../utils/Schema";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

function EditGame() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [ruleType, setRuleType] = useState("");
  const [checkboxOptions, setCheckboxOptions] = useState([]);
  const [scoring, setScoring] = useState({});
  const [isPending, startTransition] = useTransition();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await gameManagementApi.getById({ id });
        const responseData = response?.data?.data;
        setData(responseData);
        const rule = responseData?.rules?.[0] || {};
        const { ruleType, allowedPositions = [], allowedPoints = [] } = rule;
        setRuleType(ruleType);

        if (ruleType === "negative-points") {
          setCheckboxOptions(
            allowedPoints.map((point) => ({
              ...point,
              eventTypeId: point.actionEventId,
              actionEventId: point.actionEventId,
            }))
          );
        }
        else if (ruleType === "action") {
          // Set checkboxOptions from allowedPoints directly (actions), no positions
          setCheckboxOptions(
            allowedPoints.map((action) => ({
              ...action,
              eventTypeId: action.actionEventId,
              actionEventId: action.actionEventId,
              isAllowed: action.isAllowed ?? false,
            }))
          );
        }
        else if (ruleType === "position") {
          setCheckboxOptions(
            allowedPositions.map((position) => ({
              ...position,
              isAllowed: position.isAllowed ?? false,
              allowedAction: (position.allowedAction || []).map((action) => ({
                ...action,
                isAllowed: action.isAllowed ?? false,
                eventTypeId: action.actionEventId,
                actionEventId: action.actionEventId,
              })),
            }))
          );
        }
        else {
          setCheckboxOptions([]);
        }

        // scoring fetching remains same
        const payload = {
          id: responseData?.title === "action"
            ? "6889f23631225f7a3b2ea7e4"
            : "6889f1b827046038bd05124f",
        };
        const scoringResp = await gamePointManagementApi.getById(payload);
        const scoringData = scoringResp?.data?.data;
        if (scoringData?.scoring) {
          startTransition(() => {
            setScoring(scoringData.scoring);
          });
        } else {
          setScoring([]);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);



  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: GameSchema,
    initialValues: {
      title: data?.title || "",
      budget: data?.rules?.[0]?.budgetLimit || "",
      ruleType: ruleType || "",
      checkboxes: [],
    },
    validateOnChange: false,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id: id || undefined,
          title: values.title.trim(),
          type: data?.type || "negative-point",
          description: data?.description || "",
          rules: [
            {
              ruleType,
              budgetLimit: values.budget,
              isBudgetUnlimited: false,
              maxPlayers: data?.rules?.[0]?.maxPlayers || 11,
              squadSize: data?.rules?.[0]?.squadSize || 15,
              benchPlayers: data?.rules?.[0]?.benchPlayers || 4,

              allowedPoints:
                ruleType === "negative-points" || ruleType === "action"
                  ? values.checkboxes
                    .filter(opt => opt.eventTypeId)
                    .map(opt => ({
                      value: opt.title.trim(),
                      title: opt.title.trim(),
                      isAllowed: opt.isAllowed || false,
                      actionEventId: opt.eventTypeId || opt.actionEventId,
                    }))
                  : [],

              allowedPositions:
                ruleType === "position"
                  ? values.checkboxes.map(opt => ({
                    value: opt.value,
                    title: opt.title.trim(),
                    isAllowed: opt.isAllowed || false,
                  }))
                  : [],
            },
          ],
        };

        const response = id
          ? await gameManagementApi.edit(payload)
          : await gameManagementApi.add(payload);

        toast.success(response?.data?.message, { toastId: "success" });
        
        // Navigate back to game management with preserved page and view mode
        const page = searchParams.get('page');
        const view = searchParams.get('view');
        
        if (page && view) {
          navigate(`/game-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/game-management?page=${page}`);
        } else if (view) {
          navigate(`/game-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }

  });

  useEffect(() => {
    if (checkboxOptions.length > 0) {
      formik.setFieldValue(
        "checkboxes",
        checkboxOptions.map((opt) => ({
          ...opt,
          isAllowed: opt.isAllowed ?? false,
          allowedAction: (opt.allowedAction || []).map((a) => ({
            ...a,
            isAllowed: a.isAllowed ?? false,
            actionEventId: a.actionEventId || a.eventTypeId,
          })),
        }))
      );
    } else {
      formik.setFieldValue("checkboxes", []);
    }
  }, [checkboxOptions]);

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } =
    formik;

  const handleCheckboxChange = (index, checked) => {
    const updated = [...values.checkboxes];
    updated[index].isAllowed = checked;
    setFieldValue("checkboxes", updated);
  };

  const handleTitleChange = (index, newTitle) => {
    const updated = [...values.checkboxes];
    updated[index].title = newTitle;
    setFieldValue("checkboxes", updated);
  };

  const getScoringOptions = (excludeEventIds = []) => {
    if (!scoring) return [];

    // API returns array
    if (Array.isArray(scoring)) {
      return scoring.filter(s => !excludeEventIds.includes(s.eventTypeId));
    }

    // If it's an object (old logic)
    if (typeof scoring === 'object') {
      return Object.entries(scoring)
        .filter(([key]) => !excludeEventIds.includes(Number(key)))
        .map(([key, value]) => ({
          eventTypeId: Number(key),
          eventName: value.eventName || key,
          eventStatsGroup: value.eventStatsGroup || '',
          ...value
        }));
    }

    return [];
  };


  const getFilteredScoringOptions = (currentIndex = -1) => {

    if (!values.checkboxes || !Array.isArray(values.checkboxes)) {
      return getScoringOptions([]);
    }

    const allSelectedEventIds = values.checkboxes
      .flatMap((opt, idx) => {
        const eventIds = [];
        if (opt && opt.eventTypeId) {
          eventIds.push(opt.eventTypeId);
        }
        if (opt && opt.allowedAction && Array.isArray(opt.allowedAction)) {
          eventIds.push(...opt.allowedAction.map(a => a.eventTypeId).filter(id => id));
        }
        return eventIds;
      })
      .filter(id => id);


    return getScoringOptions(allSelectedEventIds);
  };

  const getFilteredActionOptions = (currentPositionIndex, currentActionIndex) => {
    if (!values.checkboxes || !Array.isArray(values.checkboxes)) {
      return getScoringOptions([]);
    }

    const currentPosition = values.checkboxes[currentPositionIndex];

    const selectedInThisPosition = (currentPosition?.allowedAction || [])
      .map(a => a.eventTypeId)
      .filter(Boolean);

    const currentEventId = currentPosition?.allowedAction?.[currentActionIndex]?.eventTypeId;

    const excludeEventIds = selectedInThisPosition.filter(id => id !== currentEventId);

    return getScoringOptions(excludeEventIds);
  };



  const handleNegativePointChange = (index, newValue) => {
    const updated = [...values.checkboxes];
    updated[index] = {
      ...updated[index],
      eventTypeId: newValue?.eventTypeId || "",
      title: newValue?.eventName || "",
      value: newValue?.eventName || "",
      actionEventId: newValue?.eventTypeId || "",
    };
    setFieldValue("checkboxes", updated);
  };

  // Add new negative point
  const addNegativePoint = () => {
    const updated = [...values.checkboxes];
    const newItem = {
      value: `negative-${Date.now()}`,
      eventTypeId: "",
      title: "",
      isAllowed: false,
      actionEventId: "",
    };
    updated.push(newItem);


    setFieldValue("checkboxes", updated);
  };

  // Remove negative point
  const removeNegativePoint = (index) => {
    const updated = [...values.checkboxes];
    updated.splice(index, 1);
    setFieldValue("checkboxes", updated);
  };


  useEffect(() => {
    if (data?.rules?.length) {
      const rule = data.rules[0];

      if (rule.ruleType === "position") {
        // ✅ positions
        setCheckboxOptions(
          rule.allowedPositions.map((pos) => ({
            ...pos,
            isAllowed: pos.isAllowed ?? false,
            allowedAction: pos.allowedAction || [],
          }))
        );
      } else if (rule.ruleType === "negative-points" || rule.ruleType === "action") {
        // ✅ negative-points and action both use allowedPoints
        setCheckboxOptions(
          rule.allowedPoints.map((pt) => ({
            ...pt,
            isAllowed: pt.isAllowed ?? false,
            eventTypeId: pt.actionEventId, // map to eventTypeId for dropdown binding
          }))
        );
      }
    }
  }, [data]);


  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Game`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    inputProps={{ maxLength: 24 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Budget"
                    required={ruleType !== "unlimited_budget"}
                    name="budget"
                    value={
                      ruleType === "unlimited-budget"
                        ? "Unlimited"
                        : values.budget
                    }
                    onChange={handleChange}
                    error={Boolean(touched.budget && errors.budget)}
                    helperText={touched.budget && errors.budget}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "decimal",
                      pattern: "[0-9.]*",
                    }}
                    disabled={ruleType === "unlimited-budget"}
                  />
                </Grid>

                {values.checkboxes?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {ruleType === "position" ? "Allowed Positions" : "Allowed Events"}
                    </Typography>

                    {values.checkboxes.map((option, index) => (
                      <Box
                        key={option._id || index}
                        sx={{ mb: 2, borderBottom: "1px solid #ddd", pb: 2 }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={option.isAllowed}
                                  onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                                />
                              }
                              label=""
                            />
                          </Grid>

                          {ruleType === "position" ? (
                            // ✅ Position ruleType → text field for position
                            <Grid item xs={9}>
                              <TextField
                                fullWidth
                                value={option.title}
                                onChange={(e) => handleTitleChange(index, e.target.value)}
                              />
                            </Grid>
                          ) : (
                            // ✅ Action & Negative-points → event dropdown
                            <Grid item xs={7}>
                              <Autocomplete
                                fullWidth
                                options={getFilteredScoringOptions(index)}
                                getOptionLabel={(opt) => opt?.eventName || ""}
                                value={
                                  getScoringOptions().find(
                                    (s) =>
                                      s.eventTypeId ===
                                      values.checkboxes[index]?.eventTypeId
                                  ) || null
                                }
                                onChange={(e, newValue) =>
                                  handleNegativePointChange(index, newValue)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Search event"
                                    label="Select Event"
                                  />
                                )}
                              />
                            </Grid>
                          )}

                          {/* ✅ Remove button for action/negative-points */}
                          {(ruleType === "negative-points" || ruleType === "action") && (
                            <Grid item xs={2}>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => removeNegativePoint(index)}
                                disabled={values.checkboxes.length === 1} // min 1 required
                              >
                                Remove
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}

                    {/* ✅ Add Event button for action/negative-points */}
                    {(ruleType === "negative-points" || ruleType === "action") && (
                      <Box mt={2}>
                        <LoadingButton
                          variant="outlined"
                          size="small"
                          onClick={addNegativePoint}
                        >
                          + Add Event
                        </LoadingButton>
                      </Box>
                    )}
                  </Grid>
                )}



                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update Game" : "Add Game"}
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default EditGame;